import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { setupScrollAnimations } from '../utils/scrollAnimations';
import './style.css';
import AboutImage from './images/about.jpg';
import MainImage from './images/Main.jpg';
import SteelMainImage from './images/SteelMain.jpg';
import MarbleMainImage from './images/MarbleMain.jpg';
import ContractingMainImage from './images/ContractingMain.jpg';

const Home = () => {
  const { t } = useTranslation();
  
  useEffect(() => {
    setupScrollAnimations();
  }, []);
  
  const contents = [
    {
      title: t('home.hero.welcome'),
      text: t('home.hero.welcomeText'),
      backgroundImage: MainImage
    },
    {
      title: t('services.steel.title'),
      text: t('services.steel.subtitle'),
      backgroundImage: SteelMainImage,
      buttonText: t('common.learnMore'),
      buttonLink: '/steel'
    },
    {
      title: t('services.marble.title'),
      text: t('services.marble.subtitle'),
      backgroundImage: MarbleMainImage,
      buttonText: t('common.learnMore'),
      buttonLink: '/marble'
    },
    {
      title: t('services.contracting.title'),
      text: t('services.contracting.subtitle'),
      backgroundImage: ContractingMainImage,
      buttonText: t('common.learnMore'),
      buttonLink: '/contracting'
    }
  ];

  const [index, setIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const nextContent = () => {
    setIndex((prev) => (prev + 1) % contents.length);
  };

  const prevContent = () => {
    setIndex((prev) => (prev - 1 + contents.length) % contents.length);
  };

  // Auto-advance carousel every 8 seconds (works on both mobile and desktop)
  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % contents.length);
    }, 8000); // 8 seconds

    return () => clearInterval(interval);
  }, [contents.length]);

  // Touch handlers for swipe functionality
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextContent();
    } else if (isRightSwipe) {
      prevContent();
    }
  };

  const goToSlide = (slideIndex) => {
    setIndex(slideIndex);
  };

  return (
    <main className="home-main">
      {/* Content box with arrows at the top */}
      <div className="home-hero-section animate-on-scroll">
        <div 
          className="content-box-fullwidth"
          style={{ 
            backgroundImage: `url(${contents[index].backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            transition: 'background-image 0.8s ease-in-out'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="content-overlay">
            <button className="arrow left" onClick={prevContent}>❮</button>
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="content-inner"
              >
                <h1>{contents[index].title}</h1>
                <p>{contents[index].text}</p>
                {contents[index].buttonText && contents[index].buttonLink && (
                  <a href={contents[index].buttonLink} className="carousel-learn-more-btn">
                    {contents[index].buttonText}
                  </a>
                )}
              </motion.div>
            </AnimatePresence>
            <button className="arrow right" onClick={nextContent}>❯</button>
          </div>
          
          {/* Carousel Dots */}
          <div className="carousel-dots">
            {contents.map((_, slideIndex) => (
              <button
                key={slideIndex}
                className={`carousel-dot ${index === slideIndex ? 'active' : ''}`}
                onClick={() => goToSlide(slideIndex)}
                aria-label={`Go to slide ${slideIndex + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
      
      <div className="home-container">
        
        {/* Additional content below */}
        <div className="home-additional-content">
          {/* Company Overview with Image Space */}
          <div className="company-overview-section animate-on-scroll">
            <div className="company-overview">
              <h2>{t('home.company.title')}</h2>
              <p>{t('home.company.description')}</p>
              
              <div className="company-mission">
                <h3>{t('home.company.mission')}</h3>
                <p>{t('home.company.missionText')}</p>
              </div>
              
              <div className="company-values">
                <h3>{t('home.company.values')}</h3>
                <p>{t('home.company.valuesText')}</p>
              </div>
            </div>
            <div className="company-image-placeholder">
                <img src={AboutImage} alt="United Ayaat Company" className="company-image" />
            </div>
          </div>
          
          {/* Departments and Services with Image Space */}
          <div className="departments-services-section animate-on-scroll">
            <div className="departments-grid">
              <div className="department-card">
                <h3>{t('home.departments.title')}</h3>
                <ul>
                  <li>{t('home.departments.marketing')}</li>
                  <li>{t('home.departments.import')}</li>
                  <li>{t('home.departments.projects')}</li>
                  <li>{t('home.departments.factory')}</li>
                  <li>{t('home.departments.architectural')}</li>
                  <li>{t('home.departments.production')}</li>
                  <li>{t('home.departments.financial')}</li>
                </ul>
              </div>
              
              <div className="services-card">
                <h3>{t('home.services.title')}</h3>
                <ul>
                  <li>{t('home.services.stoneSupply')}</li>
                  <li>{t('home.services.sculpturing')}</li>
                  <li>{t('home.services.scaleWorks')}</li>
                  <li>{t('home.services.adaptations')}</li>
                  <li>{t('home.services.installation')}</li>
                  <li>{t('home.services.design')}</li>
                  <li>{t('home.services.drawings')}</li>
                </ul>
              </div>
            </div>
            
            <div className="services-image-placeholder">
              <div className="image-placeholder">
                <span>Services Image</span>
                <p>Add services showcase here</p>
              </div>
            </div>
          </div>
          
          {/* Company Features with Image Space */}
          <div className="features-section animate-on-scroll">
            <div className="company-features">
              <div className="feature-item">
                <div className="feature-icon">🏢</div>
                <div className="feature-content">
                  <h4>{t('home.features.oneStop')}</h4>
                  <p>{t('home.features.oneStopText')}</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-icon">👥</div>
                <div className="feature-content">
                  <h4>{t('home.features.staff')}</h4>
                  <p>{t('home.features.staffText')}</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-icon">🏗️</div>
                <div className="feature-content">
                  <h4>{t('home.features.architectural')}</h4>
                  <p>{t('home.features.architecturalText')}</p>
                </div>
              </div>
            </div>
            
            <div className="features-image-placeholder">
              <div className="image-placeholder">
                <span>Features Image</span>
                <p>Add features showcase here</p>
              </div>
            </div>
          </div>
          
          {/* Gallery Section */}
          <div className="gallery-section animate-on-scroll">
            <h2>{t('home.gallery.title')}</h2>
            <div className="gallery-grid">
              <div className="gallery-item">
                <div className="image-placeholder">
                  <span>Project 1</span>
                </div>
              </div>
              <div className="gallery-item">
                <div className="image-placeholder">
                  <span>Project 2</span>
                </div>
              </div>
              <div className="gallery-item">
                <div className="image-placeholder">
                  <span>Project 3</span>
                </div>
              </div>
              <div className="gallery-item">
                <div className="image-placeholder">
                  <span>Project 4</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Home;
